import {
  ACCESS,
  MAX_ACTIVE_FINANCE_HOLDERS,
  fmtDate,
  type AccessLevel,
  type User,
} from '@gdec/shared';

/** Access levels an admin may assign directly. Main Access is not one of them. */
export const ASSIGNABLE_ACCESS: AccessLevel[] = (Object.values(ACCESS) as AccessLevel[]).filter(
  (a) => a !== ACCESS.MAIN_ACCESS,
);

export function splitName(name: string): { first: string; last: string } {
  const parts = name.trim().split(/\s+/);
  return { first: parts[0] ?? '', last: parts.slice(1).join(' ') };
}

export function departmentsOf(users: readonly User[]): string[] {
  return [...new Set(users.filter((u) => u.active).map((u) => u.department))].sort();
}

export function usersWithAccess(users: readonly User[], access: AccessLevel): User[] {
  return users.filter((u) => u.active && u.systemAccess === access);
}

/**
 * How many Finance seats are taken, ignoring one person.
 *
 * `exceptUserId` is the employee being edited: promoting somebody who already
 * holds the role does not consume a new seat, which is exactly how the server
 * counts it. Round 2 merged Finance Approver and Treasury into this one role,
 * so the prototype's three-seat cap now applies to it.
 */
export function financeSeatsTaken(users: readonly User[], exceptUserId?: string): number {
  return users.filter(
    (u) => u.active && u.systemAccess === ACCESS.FINANCE_REVIEW && u.id !== exceptUserId,
  ).length;
}

export function financeCapReached(users: readonly User[], exceptUserId?: string): boolean {
  return financeSeatsTaken(users, exceptUserId) >= MAX_ACTIVE_FINANCE_HOLDERS;
}

export function managerName(users: readonly User[], reportsTo: string | null): string {
  if (!reportsTo) return '— (top of chart)';
  return users.find((u) => u.id === reportsTo)?.name ?? '—';
}

/** `separationDate` arrives as an ISO date string, not the epoch ms fmtDate takes. */
export function formatDateString(value: string | null | undefined): string {
  if (!value) return '—';
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? value : fmtDate(ts);
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function looksLikeEmail(value: string): boolean {
  return EMAIL_RE.test(value);
}
